import { useEffect, useState } from 'react';
import { FormControlLabel, Switch } from '@mui/material';
import { set } from 'lodash';
import { Autocomplete, Input } from '../../../../../../components';
import { Condominium, CondominiumType, Laundry } from '../../../../../../entities';
import { RecursiveKeyOf } from '../../../../../../utils/types';
import { clone } from '../../../../../../utils/helpers';
import useStyles from './styles';
import ConfigurationPanel from './ConfigurationPanel';
import { Configs, ConfigurationType } from './types';
import Furniture from '../../../../../../entities/furniture.entity';
import Cleaning from '../../../../../../entities/cleaning.entity';
import Maid from '../../../../../../entities/maid.entity';
import ConfirmationDialog from '../../../../../../components/ConfirmationDialog';

interface ConfigurationPanelProps {
  laundries: Laundry[];
  furnitures: Furniture[];
  cleanings: Cleaning[];
  maids: Maid[];
  condominium: Condominium;
  handleFieldChange: (target: RecursiveKeyOf<Condominium>, value: string | boolean | number | null) => void;
  setValue: (target: RecursiveKeyOf<Condominium>, value: string | boolean | number | null) => void;
}

export const Configurations = ({
  condominium,
  laundries,
  furnitures,
  cleanings,
  maids,
  handleFieldChange,
  setValue,
}: ConfigurationPanelProps): JSX.Element => {
  const styles = useStyles();
  const [configs, setConfigs] = useState<Configs>({} as Configs);
  const [disableCondominiumModal, setDisableCondominiumModal] = useState(false);

  useEffect(() => {
    if (Object.keys(condominium).length === 0) return;
    setConfigs(condominium);
  }, [condominium]);

  const handleChange = (property: keyof Configs, value: string | boolean | number | null) => {
    const obj = set(clone(configs), property, value);
    setConfigs(obj);

    setValue(property, value);
    handleFieldChange(property, value);
  };

  async function handleAcceptModal() {
    handleChange('isDisabled', !configs.isDisabled);
    setDisableCondominiumModal(false);
  }

  return (
    <div className={styles.wrapper}>
      <ConfigurationPanel type={ConfigurationType.Setup}>
        <div className={styles.line}>
          <div className={styles.item}>
            <FormControlLabel
              label="Desativar condomínio"
              control={
                <Switch
                  value={configs.isDisabled ?? false}
                  checked={configs.isDisabled ?? false}
                  onClick={() => {
                    const value = !configs.isDisabled;
                    setDisableCondominiumModal(!disableCondominiumModal);
                  }}
                />
              }
            />
          </div>

          <div className={styles.item}>
            <FormControlLabel
              label="Condomínio comercial"
              control={
                <Switch
                  value={configs.type === CondominiumType.Commercial}
                  checked={configs.type === CondominiumType.Commercial}
                  onChange={() => {
                    const value =
                      configs.type === CondominiumType.Residential
                        ? CondominiumType.Commercial
                        : CondominiumType.Residential;

                    handleChange('type', value);
                  }}
                />
              }
            />
          </div>

          <div className={styles.item}>
            <FormControlLabel
              label="Habilitar cadastros"
              control={
                <Switch
                  value={configs.isSignupEnabled ?? true}
                  checked={configs.isSignupEnabled ?? true}
                  onChange={() => handleChange('isSignupEnabled', !configs.isSignupEnabled)}
                />
              }
            />
          </div>
        </div>
        <div className={styles.line}>
          <div className={styles.item}>
            <FormControlLabel
              label="Habilitar feed"
              control={
                <Switch
                  value={configs.isFeedEnabled ?? true}
                  checked={configs.isFeedEnabled ?? true}
                  onChange={() => handleChange('isFeedEnabled', !configs.isFeedEnabled)}
                />
              }
            />
          </div>

          <div className={styles.item}>
            <FormControlLabel
              label="Habilitar postagem morador"
              control={
                <Switch
                  value={configs.isDwellerPostEnabled ?? true}
                  checked={configs.isDwellerPostEnabled ?? true}
                  onChange={() => handleChange('isDwellerPostEnabled', !configs.isDwellerPostEnabled)}
                />
              }
            />
          </div>

          <div className={styles.item}>
            <FormControlLabel
              label="Habilitar comentários morador"
              control={
                <Switch
                  value={configs.isDwellerCommentEnabled ?? true}
                  checked={configs.isDwellerCommentEnabled ?? true}
                  onChange={() => handleChange('isDwellerCommentEnabled', !configs.isDwellerCommentEnabled)}
                />
              }
            />
          </div>
        </div>

        <div className={styles.line}>
          <div className={styles.item}>
            <FormControlLabel
              label="Habilitar ajuda"
              control={
                <Switch
                  checked={configs.isHelpEnabled ?? true}
                  onChange={() => handleChange('isHelpEnabled', !configs.isHelpEnabled)}
                />
              }
            />
          </div>

          <div className={styles.item}>
            <FormControlLabel
              label="Habilitar chat"
              control={
                <Switch
                  value={configs.isChatEnabled ?? true}
                  checked={configs.isChatEnabled ?? true}
                  onChange={() => handleChange('isChatEnabled', !configs.isChatEnabled)}
                />
              }
            />
          </div>

          <div className={styles.item}>
            <FormControlLabel
              label="Habilitar alerta de emergência"
              control={
                <Switch
                  value={configs.isEmergencyEnabled ?? true}
                  checked={configs.isEmergencyEnabled ?? true}
                  onChange={() => handleChange('isEmergencyEnabled', !configs.isEmergencyEnabled)}
                />
              }
            />
          </div>
        </div>
      </ConfigurationPanel>

      <ConfigurationPanel type={ConfigurationType.Facilities}>
        <div className={styles.line}>
          <div className={styles.item}>
            <FormControlLabel
              label="Habilitar visitantes"
              control={
                <Switch
                  value={configs.isGuestsEnabled ?? true}
                  checked={configs.isGuestsEnabled ?? true}
                  onChange={() => handleChange('isGuestsEnabled', !configs.isGuestsEnabled)}
                />
              }
            />
          </div>

          <div className={styles.item}>
            <FormControlLabel
              label="Habilitar reservas"
              control={
                <Switch
                  value={configs.isReservationsEnabled ?? true}
                  checked={configs.isReservationsEnabled ?? true}
                  onChange={() => handleChange('isReservationsEnabled', !configs.isReservationsEnabled)}
                />
              }
            />
          </div>
        </div>

        <div className={styles.line}>
          <div className={styles.item}>
            <FormControlLabel
              label="Habilitar bicicletário"
              control={
                <Switch
                  value={configs.isBikesEnabled ?? true}
                  checked={configs.isBikesEnabled ?? true}
                  onChange={() => handleChange('isBikesEnabled', !configs.isBikesEnabled)}
                />
              }
            />
          </div>

          <div className={styles.item}>
            <FormControlLabel
              label="Habilitar encomendas"
              control={
                <Switch
                  value={configs.isPackagesEnabled ?? true}
                  checked={configs.isPackagesEnabled ?? true}
                  onChange={() => handleChange('isPackagesEnabled', !configs.isPackagesEnabled)}
                />
              }
            />
          </div>
        </div>
        <div className={styles.line}>
          <Input
            label="Dias disponiveis para reserva"
            placeholder="Ex: 30"
            type="number"
            value={configs.reservationMaxPeriod?.toString()}
            disabled={!configs.isReservationsEnabled}
            onChange={(e) => handleChange('reservationMaxPeriod', Number(e.target.value))}
          />
        </div>
      </ConfigurationPanel>

      <ConfigurationPanel type={ConfigurationType.Partners}>
        <div className={styles.line}>
          <div className={styles.item}>Ao selecionar o parceiro, verifique se o mesmo atende esta região</div>
        </div>

        <div className={styles.line}>
          <div className={styles.item}>
            <Autocomplete
              options={laundries}
              label="Lavanderias"
              value={laundries.find(({ id }) => id === configs.laundryId) || null}
              getOptionLabel={({ label }) => label}
              placeholder="Selecione a lavanderia"
              onChange={(e, data) => {
                handleChange('laundryId', data?.id || null);
              }}
            />
          </div>
          <div className={styles.item}>
            <Autocomplete
              options={furnitures}
              label="Móveis"
              value={furnitures.find(({ id }) => id === configs.furnitureId) || null}
              getOptionLabel={({ label }) => label}
              placeholder="Selecione a loja de Móvel"
              onChange={(e, data) => {
                handleChange('furnitureId', data?.id || null);
              }}
            />
          </div>
          <div className={styles.item}>
            <Autocomplete
              options={cleanings}
              label="Diarista"
              value={cleanings.find(({ id }) => id === configs.cleaningId) || null}
              getOptionLabel={({ label }) => label}
              placeholder="Selecione a Diarista"
              onChange={(e, data) => {
                handleChange('cleaningId', data?.id || null);
              }}
            />
          </div>
          <div className={styles.item}>
            <Autocomplete
              options={maids}
              label="Camareira"
              value={maids.find(({ id }) => id === configs.maidId) || null}
              getOptionLabel={({ label }) => label}
              placeholder="Selecione a Camareira"
              onChange={(e, data) => {
                handleChange('maidId', data?.id || null);
              }}
            />
          </div>
        </div>

        <ConfirmationDialog
          open={disableCondominiumModal}
          content="Deseja continuar esta operação?"
          onClose={() => {
            setDisableCondominiumModal(false);
          }}
          onCancel={() => {
            setDisableCondominiumModal(false);
          }}
          onAccept={() => {
            handleAcceptModal();
          }}
        />
      </ConfigurationPanel>
    </div>
  );
};

export default Configurations;
